// Identity (AD) on the shared policy screens: the reference adapter (see
// ../adapterContract.js). Who = AD users, groups and OUs; Where = Domain,
// Protocols and Destinations; the MFA prompt sits under Require MFA. No Source
// field: the Network condition replaces it (stored ad.sources show as Network
// "Only from" entries and are cleared on save). "Count matching users" and
// "Test a sign-in" sit under the editor's summary line.
import React, { useEffect, useState } from "react";
import { AutoComplete, Button, Checkbox, Input, Radio, Select } from "antd";

import { AN_COLORS, AN_MONO } from "../../../design-system/tokens";
import { DOMAIN_ID, ORG_ID } from "../../../constants";
import {
  createADPolicy,
  getAllADDomains,
  getEffectivePolicy,
  listADGroups,
  listADPolicies,
  listADUsers,
  listOUs,
  policyAction,
  previewPolicyImpact,
  updateADPolicy,
} from "../../../service/adPolicyService";
import { deletePolicyJSON } from "../../../service/appService";
import {
  DEFAULT_MFA_PROMPT,
  MFA_VARIABLES,
  PROTOCOLS,
  cleanTokens,
  domainFqdn,
  domainLabel,
  isValidDestination,
  renderMfaPrompt,
  sanitizeProtocols,
  unknownVariables,
} from "../../enduser/ad-policies/adPolicyModel";
import DiscoverFromADDrawer from "../../enduser/ad-policies/DiscoverFromADDrawer";
import { AD_NETWORK_NOTE } from "../../../components/policyShell/signals";
import { callService, defineAdapter, listFilters, updatedPolicyId } from "../adapterContract";
import { Field, Muted, Note, TokenInput } from "../ui";
import { fieldDomId } from "../editor/draft";

const PAGE_SIZE = 50;
const ids = () => ({ orgId: ORG_ID(), tenantId: DOMAIN_ID() });

// Directory lookups reject with an Error whose message the picker shows.
const lookup = (fn, payload, fallback) =>
  new Promise((resolve, reject) =>
    fn({ payload, onSuccess: resolve, onFailure: (e) => reject(new Error(e?.message || e?.data?.message || fallback)) }),
  );

const isAdDomain = (d) => String(d?.identity_source || "AD").toLowerCase() !== "entraid";

/* ---------------------------------------------------------------- lookups */

const searchUsers = (q, { draft }) =>
  lookup(listADUsers, { org_Id: ORG_ID(), tenant_Id: DOMAIN_ID(), adId: draft.product.adId, filter: q, page: 1, pageSize: PAGE_SIZE }, "Couldn't load AD users.").then(
    (res) => (Array.isArray(res?.data) ? res.data : []).map((u) => ({ value: u.username, label: u.username, sub: u.logoname || u.email || "" })),
  );

const searchGroups = (q, { draft }) =>
  lookup(listADGroups, { org_Id: ORG_ID(), tenant_Id: DOMAIN_ID(), adId: draft.product.adId, filter: q, page: 1, pageSize: PAGE_SIZE }, "Couldn't load AD groups.").then(
    (res) =>
      (Array.isArray(res?.data) ? res.data : []).map((g) => ({
        value: g.groupName,
        label: g.groupName,
        sub: g.memberCount != null ? `${g.memberCount} ${g.memberCount === 1 ? "user" : "users"}` : "",
      })),
  );

// listOU answers with every OU the tenant knows, keyed by ad_id: keep this
// domain's. Cached per domain; filtered here as the admin types.
const ouCache = new Map();
const searchOus = (q, { draft }) => {
  const { adId, domain } = draft.product;
  const key = String(adId);
  if (!ouCache.has(key)) {
    ouCache.set(
      key,
      lookup(listOUs, { org_Id: ORG_ID(), tenant_Id: DOMAIN_ID(), adId, domain }, "Couldn't load OUs.")
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.ous) ? res.ous : [];
          return list
            .filter((ou) => {
              if (typeof ou === "string") return true;
              const ouAdId = ou?.ad_id ?? ou?.adId;
              if (ouAdId != null) return Number(ouAdId) === Number(adId);
              const adName = ou?.ad_name || ou?.adName;
              return !adName || adName === domain;
            })
            .map((ou) => (typeof ou === "string" ? ou : ou?.ou_name || ou?.ouName || ou?.name || ou?.dn))
            .filter(Boolean);
        })
        .catch((e) => {
          ouCache.delete(key);
          throw e;
        }),
    );
  }
  const needle = q.trim().toLowerCase();
  return ouCache.get(key).then((list) => list.filter((v) => !needle || v.toLowerCase().includes(needle)).map((v) => ({ value: v })));
};

const needsDomain = (draft) => (draft.product.adId != null || draft.product.domain ? null : "Pick a domain first");
const domainDeps = (draft) => [draft.product.adId];

/* ------------------------------------------------------------------- data */

function useAdData(draft, setProduct) {
  const [domains, setDomains] = useState(null);
  useEffect(() => {
    let live = true;
    getAllADDomains({
      payload: ids(),
      onSuccess: (res) => live && setDomains((res?.domains || res?.data || []).filter(isAdDomain)),
      onFailure: () => live && setDomains([]),
    });
    return () => {
      live = false;
    };
  }, []);

  // Resolve the policy's domain once the list is in: by stored id, else by
  // FQDN (policies written before ad.adId), else the first one for a new policy.
  const adId = draft?.product?.adId;
  const fqdn = draft?.product?.domain;
  useEffect(() => {
    if (!draft || !domains || !domains.length) return;
    if (adId != null && domains.some((d) => d.id === adId)) return;
    const byName = fqdn ? domains.find((d) => domainFqdn(d) === fqdn || domainLabel(d) === fqdn) : null;
    if (byName) setProduct({ adId: byName.id, domain: domainFqdn(byName) }, { silent: true });
    else if (adId == null && !fqdn) setProduct({ adId: domains[0].id, domain: domainFqdn(domains[0]) }, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domains, adId, fqdn]);

  return { domains: domains || [], domainsLoaded: domains !== null };
}

const domainName = (product, data) => {
  const d = (data?.domains || []).find((x) => x.id === product.adId);
  return d ? domainLabel(d) : product.domain || "";
};

/* -------------------------------------------------------------- sections */

/**
 * The Domain (AD) or Directory (RADIUS) picker, first in Where. Writes
 * product.adId and product.domain (the FQDN). errorKey: "where.domain".
 */
export function DomainField({ product, setProduct, errors, touch, data, label = "Domain", errorKey = "where.domain", empty = "No AD domains yet" }) {
  const options = (data?.domains || []).map((d) => ({ value: d.id, label: domainLabel(d), fqdn: domainFqdn(d) }));
  const value = options.some((o) => o.value === product.adId) ? product.adId : product.domain || undefined;
  const noun = label.toLowerCase();
  return (
    <Field label={label} required error={errors[errorKey]} htmlFor={fieldDomId(errorKey)}>
      <Select
        id={fieldDomId(errorKey)}
        aria-label={label}
        showSearch
        value={value}
        placeholder={data?.domainsLoaded ? `Pick a ${noun}` : `Loading ${noun}s…`}
        options={options}
        onChange={(id) => {
          const d = (data?.domains || []).find((x) => x.id === id);
          setProduct({ adId: id, domain: domainFqdn(d) });
        }}
        onBlur={() => touch(errorKey)}
        status={errors[errorKey] ? "error" : undefined}
        filterOption={(input, o) => `${o.label} ${o.fqdn}`.toLowerCase().includes(input.toLowerCase())}
        optionRender={(o) => (
          <div>
            <div>{o.data.label}</div>
            <div style={{ fontFamily: AN_MONO, fontSize: "0.75rem", color: AN_COLORS.muted }}>{o.data.fqdn || "no FQDN reported"}</div>
          </div>
        )}
        notFoundContent={data?.domainsLoaded ? empty : "Loading…"}
        style={{ width: "100%" }}
      />
    </Field>
  );
}

const DEST_MESSAGE = "Use cifs/fs01.corp.lab, cifs/*, fs01.corp.lab or *.";

function Where(props) {
  const { product, setProduct, errors, touch } = props;
  const protocols = product.protocols || [];
  return (
    <>
      <DomainField {...props} />
      <Field label="Protocols" help="None picked means all four. NTLM and SMB match each other.">
        <Checkbox.Group
          aria-label="Protocols"
          value={protocols}
          onChange={(v) => setProduct({ protocols: sanitizeProtocols(v) })}
          options={PROTOCOLS.map((p) => ({ value: p.value, label: p.label }))}
        />
      </Field>
      <Field label="Destinations" error={errors["where.destinations"]}>
        <div id={fieldDomId("where.destinations")} style={{ display: "grid", gap: "0.5rem", scrollMarginTop: "1rem" }}>
          <Radio.Group value={product.destMode || "all"} onChange={(e) => setProduct({ destMode: e.target.value })} aria-label="Destinations">
            <Radio value="all">All destinations</Radio>
            <Radio value="specific">Specific SPNs or hosts</Radio>
          </Radio.Group>
          {product.destMode === "specific" && (
            <TokenInput
              ariaLabel="Destinations"
              value={product.destinations}
              onChange={(v) => setProduct({ destinations: v })}
              onBlur={() => touch("where.destinations")}
              validate={(v) => (isValidDestination(v) ? true : DEST_MESSAGE)}
              placeholder="cifs/fs01.corp.lab"
              status={errors["where.destinations"] ? "error" : undefined}
            />
          )}
        </div>
      </Field>
    </>
  );
}

function MfaPrompt({ product, setProduct, action, data }) {
  if (action !== "mfa_required") return null;
  const value = product.mfaPrompt ?? DEFAULT_MFA_PROMPT;
  const unknown = unknownVariables(value);
  const preview = renderMfaPrompt(value, { $domain: domainName(product, data) || undefined });
  const insert = (token) => setProduct({ mfaPrompt: `${value}${value && !value.endsWith(" ") ? " " : ""}${token}` });
  return (
    <Field label="MFA prompt" help={unknown.length ? null : `Preview: ${preview}`} error={unknown.length ? `${unknown.join(", ")} ${unknown.length > 1 ? "aren't variables" : "isn't a variable"} and will show as typed.` : null}>
      <Input.TextArea aria-label="MFA prompt" value={value} onChange={(e) => setProduct({ mfaPrompt: e.target.value })} autoSize={{ minRows: 2, maxRows: 4 }} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem", alignItems: "center" }}>
        <Muted>Insert:</Muted>
        {MFA_VARIABLES.map((v) => (
          <Button key={v.token} size="small" onClick={() => insert(v.token)} title={v.desc} style={{ fontFamily: AN_MONO, fontSize: "0.75rem" }}>
            {v.token}
          </Button>
        ))}
      </div>
    </Field>
  );
}

const ACTION_WORDS = { allow: "Allow", mfa_required: "Require MFA", block: "Deny", deny: "Deny" };

function TestSignIn({ draft, data }) {
  const [user, setUser] = useState("");
  const [protocol, setProtocol] = useState("kerberos");
  const [options, setOptions] = useState([]);
  const [state, setState] = useState({ status: "idle", result: null });
  const fqdn = draft.product.domain;
  const search = (q) =>
    searchUsers(q, { draft, data })
      .then((list) => setOptions(list.map((o) => ({ value: o.value }))))
      .catch(() => setOptions([]));
  const run = () => {
    setState({ status: "loading", result: null });
    getEffectivePolicy({
      payload: { org_id: ORG_ID(), tenant_id: DOMAIN_ID(), ad_user: user.trim(), domain: fqdn, protocol },
      onSuccess: (res) => setState({ status: "ready", result: res?.data && res.data.action ? res.data : res }),
      onFailure: () => setState({ status: "error", result: null }),
    });
  };
  const r = state.result || {};
  const action = ACTION_WORDS[String(r.action || (r.mfa_required ? "mfa_required" : "")).toLowerCase()] || r.action;
  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <Muted>Checks a sign-in against the policies that are live now. This policy counts once it's saved.</Muted>
      <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "flex-end" }}>
        <Field label="AD user" style={{ flex: "1 1 14rem" }}>
          <AutoComplete aria-label="AD user" value={user} onChange={setUser} onSearch={search} onFocus={() => search("")} options={options} placeholder="jdoe" style={{ width: "100%" }} />
        </Field>
        <Field label="Protocol">
          <Select aria-label="Protocol" value={protocol} onChange={setProtocol} options={PROTOCOLS} style={{ width: "9rem" }} />
        </Field>
        <Button onClick={run} disabled={!user.trim()} loading={state.status === "loading"}>
          Test
        </Button>
      </div>
      {state.status === "ready" && (
        <div style={{ fontSize: "0.875rem", color: AN_COLORS.ink2 }}>
          {action ? `${action}.` : "No policy matches."} {r.match_reason ? <Muted>{r.match_reason}</Muted> : null}
        </div>
      )}
      {state.status === "error" && <Note tone="danger">Couldn't run the test. Try again.</Note>}
    </div>
  );
}

function DiscoverButton({ reload, data }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>Discover from AD</Button>
      <DiscoverFromADDrawer
        open={open}
        onClose={() => setOpen(false)}
        domains={data?.domains || []}
        onApplied={() => {
          setOpen(false);
          reload();
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------ words */

const protocolText = (list) => {
  const p = sanitizeProtocols(list);
  return p.length ? p.map((v) => PROTOCOLS.find((x) => x.value === v)?.label || v).join(", ") : "All protocols";
};
const orWords = (items) => (items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`);
const sentenceWhere = (draft, data) => {
  const p = draft.product;
  const name = domainName(p, data);
  const protos = sanitizeProtocols(p.protocols).map((v) => PROTOCOLS.find((x) => x.value === v)?.label || v);
  const dst = p.destMode === "specific" ? cleanTokens(p.destinations) : [];
  return [name && `in ${name}`, protos.length && `over ${orWords(protos)}`, dst.length && `to ${orWords(dst.length > 3 ? [...dst.slice(0, 2), `${dst.length - 2} more`] : dst)}`]
    .filter(Boolean)
    .join(" ");
};

const destText = (list) => {
  const d = cleanTokens(list);
  if (!d.length) return "";
  return d.length > 1 ? `${d[0]} +${d.length - 1}` : d[0];
};

/* --------------------------------------------------------------- adapter */

const adAdapter = defineAdapter({
  key: "ad",
  policyType: "AD",
  modeKey: "mfa_ad",
  labels: {
    product: "AD",
    eyebrow: "Identity · Policies",
    listDescription: "Who signs in to Active Directory with MFA, and who is allowed or denied.",
    emptySentence: "Without a policy, sign-ins are allowed.",
    subject: "sign-in",
  },

  list: () =>
    callService(listADPolicies, { ...ids(), policyType: "AD", filters: listFilters("AD"), page: 1, limit: 500 }).then((res) =>
      (res?.policies || res?.data || []).filter((p) => String(p?.policyType || p?.policyJson?.policyType || "").toLowerCase() === "ad"),
    ),
  create: ({ policyName, policyJson, force }) =>
    callService(createADPolicy, { ...ids(), policyName, policyType: "AD", policyJson, generatedBy: "Manual", ...(force ? { force: true } : {}) }).then((res) => ({
      id: typeof res?.data === "string" ? res.data : null,
      warning: res?.warning || "",
    })),
  // The service makes a new policy (new id) on every update.
  update: ({ id, policyName, policyJson, stored }) =>
    callService(updateADPolicy, {
      id,
      ...ids(),
      policyName,
      policyType: "AD",
      policyJson,
      status: stored?.status || "Approved",
      version: (Number(stored?.version) || 1) + 1,
      updatedBy: localStorage.getItem("userId") || "",
    }).then((res) => ({ id: updatedPolicyId(res, id), warning: res?.warning || "" })),
  // Only Approved policies are evaluated: Suspended and anything still
  // pending (e.g. applied from Discover) read as off; switching on approves.
  isActive: (row) => String(row?.status || "Approved") === "Approved",
  setActive: (row, active) =>
    callService(policyAction, { ...ids(), action: { actionType: "status", actionIds: [row.id || row.policyId], actionValue: active ? "Approved" : "Suspended" } }),
  remove: (row) => callService(deletePolicyJSON, { ...ids(), policyId: row.id || row.policyId }),

  emptyDraft: () => ({
    who: { everyone: false, users: [], groups: [], ous: [] },
    product: { adId: null, domain: "", protocols: [], destMode: "all", destinations: [], mfaPrompt: DEFAULT_MFA_PROMPT },
  }),
  fromPolicy: (row) => {
    const j = row?.policyJson || {};
    const ad = j.ad || {};
    const dst = cleanTokens(ad.destinations);
    const sources = cleanTokens(ad.sources);
    let conditions;
    if (sources.length) {
      const c = j.conditions && typeof j.conditions === "object" ? j.conditions : {};
      const include = cleanTokens([...(c.network?.include || []), ...sources]);
      conditions = { ...c, network: { ...(c.network || {}), include } };
    }
    return {
      who: { everyone: !!ad.matchAll, users: ad.users || [], groups: ad.groups || [], ous: ad.ous || [] },
      product: {
        adId: ad.adId ?? null,
        domain: ad.domain || "",
        protocols: sanitizeProtocols(j.protocolControl?.allowed),
        destMode: dst.length ? "specific" : "all",
        destinations: dst,
        mfaPrompt: j.mfaPromptTemplate || DEFAULT_MFA_PROMPT,
      },
      conditions,
    };
  },
  toPolicyJson: (draft, stored) => {
    const s = stored || {};
    const p = draft.product;
    const out = {
      ...s,
      policyType: "AD",
      policyKind: "static",
      authTypes: ["ad"],
      ad: {
        ...(s.ad || {}),
        // The id and the FQDN travel together; the display label never does.
        adId: p.adId,
        domain: p.domain,
        users: draft.who.users,
        groups: draft.who.groups,
        ous: draft.who.ous,
        matchAll: !!draft.who.everyone,
        // Now the Network condition (spec 7.3).
        sources: [],
        destinations: p.destMode === "specific" ? cleanTokens(p.destinations) : [],
      },
      protocolControl: { ...(s.protocolControl || {}), allowed: sanitizeProtocols(p.protocols) },
    };
    if (draft.action === "mfa_required") out.mfaPromptTemplate = p.mfaPrompt ?? DEFAULT_MFA_PROMPT;
    return out;
  },
  validate: (draft) => {
    const e = {};
    const p = draft.product;
    if (p.adId == null && !p.domain) e["where.domain"] = "Pick a domain.";
    if (p.destMode === "specific" && !cleanTokens(p.destinations).length) {
      e["where.destinations"] = "Add at least one destination, or choose All destinations.";
    }
    return e;
  },

  useData: useAdData,
  Where,
  who: {
    everyone: true,
    except: true,
    fields: [
      { key: "users", kind: "users", label: "Users", placeholder: "Search AD users", search: searchUsers, deps: domainDeps, disabledReason: needsDomain, except: "users" },
      { key: "groups", kind: "groups", label: "Groups", placeholder: "Search AD groups", search: searchGroups, deps: domainDeps, disabledReason: needsDomain, except: "groups" },
      { key: "ous", kind: "ous", label: "OUs", placeholder: "Search OUs", search: searchOus, deps: domainDeps, disabledReason: needsDomain },
    ],
  },

  listWhere: (row) => {
    const j = row?.policyJson || {};
    return [protocolText(j.protocolControl?.allowed), destText(j.ad?.destinations)].filter(Boolean).join(" · ");
  },
  sentenceWhere,
  reviewRows: (draft, data) => {
    const p = draft.product;
    const dst = p.destMode === "specific" ? cleanTokens(p.destinations) : [];
    const rows = [
      { key: "domain", label: "Domain", value: domainName(p, data) || "None picked", section: "where" },
      {
        key: "where",
        label: "Where",
        value: `${protocolText(p.protocols)}; ${dst.length ? dst.join(", ") : "all destinations"}`,
        section: "where",
      },
    ];
    if (draft.action === "mfa_required") rows.push({ key: "prompt", label: "MFA prompt", value: p.mfaPrompt || DEFAULT_MFA_PROMPT, section: "action" });
    return rows;
  },

  features: {
    fallback: true,
    approval: false,
    session: false,
    ActionExtra: MfaPrompt,
    TestSignIn,
    ListHeaderExtra: DiscoverButton,
    networkNote: AD_NETWORK_NOTE,
    impact: (draft, data) => {
      const name = domainName(draft.product, data);
      if (draft.who.everyone) return Promise.resolve(`Matches every user in ${name || "the domain"}.`);
      return new Promise((resolve) => {
        previewPolicyImpact({
          payload: { ...ids(), domain: draft.product.domain, adGroups: draft.who.groups, adUsers: draft.who.users, adOus: draft.who.ous },
          onSuccess: (res) => {
            const n = res?.matchedUsers ?? res?.data?.matchedUsers;
            resolve(typeof n === "number" ? `Matches ${n} ${n === 1 ? "user" : "users"}.` : null);
          },
          onFailure: () => resolve(null),
        });
      });
    },
  },
});

export default adAdapter;
